import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NUTZUNGSBEDINGUNGEN, type NutzungsbedingungenStand } from '@awf/shared';
import { ApiError } from '../api/client.js';
import { ALLGEMEINER_FEHLERTEXT } from '../api/errors.js';
import { konsumiereGeradeAngemeldet } from '../auth/gerade-angemeldet.js';
import stile from './willkommen-dialog.module.css';

export interface NutzungsDienst {
  holen(): Promise<NutzungsbedingungenStand>;
  bestaetigen(version: string): Promise<NutzungsbedingungenStand>;
}

type Stand =
  { art: 'laedt' } | { art: 'fehler'; text: string } | { art: 'offen' } | { art: 'bestaetigt' };

export const FASSUNG_ABWEICHEND =
  'Die Nutzungsbedingungen wurden aktualisiert. Bitte laden Sie die Seite neu.';

function fehlertext(f: unknown): string {
  return f instanceof ApiError ? f.text : ALLGEMEINER_FEHLERTEXT;
}

/**
 * Wird mit dem Ende der Alpha-Phase geloescht -- samt Aufruf unten. Der Dialog bleibt dann nur
 * noch fuer die Nutzungsbedingungen stehen und erscheint nur, solange sie ausstehen.
 */
function AlphaHinweis() {
  return (
    <>
      <span className="eds-chip">Alpha-Version</span>
      <p className={stile.text}>
        Erste, frühe Version mit Nutzer-Testing im kleinen Rahmen nach der Entwicklungsphase. Es
        können noch Fehler auftreten, danke für Ihr Verständnis und Feedback.
      </p>
    </>
  );
}

/**
 * Willkommen nach jeder frischen Anmeldung, wie das Alpha-Overlay der Content-Factory
 * (components/willkommen-overlay), und zugleich der Ort, an dem die Nutzungsbedingungen bestaetigt
 * werden (Beleg fuer AI-1-2). Solange die aktuelle Fassung aussteht, erscheint der Dialog auch ohne
 * frische Anmeldung und laesst sich nicht schliessen, auch nicht mit Escape. Ein Lesefehler
 * sperrt ebenfalls: Ohne Auskunft ist nichts bestaetigt. Die eigentliche Pruefung steht im Backend
 * vor den schreibenden Routen; dieser Dialog sorgt dafuer, dass niemand erst dort davon erfaehrt.
 *
 * Natives <dialog> mit showModal(): Fokusfang kommt von der Plattform. `display` steht nur
 * unter [open], sonst schluege Autor-CSS die Browserregel fuer den geschlossenen Dialog.
 */
export function WillkommenDialog({ nutzung }: { nutzung: NutzungsDienst }) {
  const dialog = useRef<HTMLDialogElement>(null);
  // Einmal je Einhaengen gelesen: der Merker ist danach verbraucht.
  const [frisch] = useState(konsumiereGeradeAngemeldet);
  const [stand, setStand] = useState<Stand>({ art: 'laedt' });
  const [gelesen, setGelesen] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const checkboxId = useId();

  useEffect(() => {
    let aktuell = true;
    setStand({ art: 'laedt' });
    nutzung.holen().then(
      (s) => {
        if (!aktuell) return;
        // Andere Fassung im Backend (versetzter Rollout, Rollback): Eine Bestaetigung DIESER
        // Fassung belegt nicht den Text, der hier steht, und bestaetigen liesse sich hier nur der
        // falsche. Also sperren und neu laden lassen (Codex-Review PR #98, MITTEL).
        if (s.version !== NUTZUNGSBEDINGUNGEN.version) {
          setStand({ art: 'fehler', text: FASSUNG_ABWEICHEND });
          return;
        }
        setStand({ art: s.bestaetigtAm ? 'bestaetigt' : 'offen' });
      },
      (f: unknown) => aktuell && setStand({ art: 'fehler', text: fehlertext(f) }),
    );
    return () => {
      aktuell = false;
    };
  }, [nutzung, versuch]);

  const pflicht = stand.art === 'offen' || stand.art === 'fehler';
  // Ohne frische Anmeldung erst oeffnen, wenn feststeht, dass etwas aussteht -- sonst blitzte der
  // Dialog bei jedem Neuladen kurz auf.
  const zeigen = frisch || pflicht;

  useEffect(() => {
    const element = dialog.current;
    // jsdom kennt showModal nicht; dort und in alten Browsern bleibt der Dialog zu. Die Sperre
    // im Backend gilt trotzdem.
    if (zeigen && element && !element.open && typeof element.showModal === 'function') {
      element.showModal();
    }
  }, [zeigen]);

  const bestaetigen = useCallback(async () => {
    setSendet(true);
    setSendeFehler(null);
    try {
      await nutzung.bestaetigen(NUTZUNGSBEDINGUNGEN.version);
      setStand({ art: 'bestaetigt' });
      dialog.current?.close();
    } catch (f) {
      setSendeFehler(fehlertext(f));
    } finally {
      setSendet(false);
    }
  }, [nutzung]);

  return (
    <dialog
      ref={dialog}
      className={stile.dialog}
      aria-labelledby="willkommen-titel"
      onCancel={(e) => {
        if (pflicht || stand.art === 'laedt') e.preventDefault();
      }}
    >
      <img className={stile.logo} src="/marke/ergo-logo.png" alt="ERGO" />
      <AlphaHinweis />
      <h2 id="willkommen-titel" className={stile.titel}>
        Willkommen zur Content Engine
      </h2>

      {stand.art === 'laedt' && (
        <p role="status" className={stile.text}>
          Nutzungsbedingungen werden geprüft …
        </p>
      )}

      {stand.art === 'fehler' && (
        <div className="eds-alert eds-alert--error" role="alert">
          <div>
            <p>{stand.text}</p>
            <button
              type="button"
              className="eds-button eds-button--secondary"
              onClick={() => setVersuch((n) => n + 1)}
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      )}

      {stand.art === 'offen' && (
        <form
          className={stile.bedingungen}
          onSubmit={(e) => {
            e.preventDefault();
            if (gelesen && !sendet) void bestaetigen();
          }}
        >
          <h3 className={stile.bedingungenTitel}>{NUTZUNGSBEDINGUNGEN.titel}</h3>
          <div className={stile.bedingungenText}>
            {NUTZUNGSBEDINGUNGEN.absaetze.map((absatz) => (
              <p key={absatz}>{absatz}</p>
            ))}
          </div>
          <label className={stile.zustimmung} htmlFor={checkboxId}>
            <input
              id={checkboxId}
              type="checkbox"
              checked={gelesen}
              onChange={(e) => setGelesen(e.target.checked)}
            />
            Ich habe die Nutzungsbedingungen gelesen und erkenne sie an.
          </label>
          {sendeFehler && (
            <div className="eds-alert eds-alert--error" role="alert">
              <p>{sendeFehler}</p>
            </div>
          )}
          <button
            type="submit"
            className="eds-button eds-button--primary"
            disabled={!gelesen || sendet}
          >
            {sendet ? 'Wird gespeichert …' : 'Bestätigen und fortfahren'}
          </button>
        </form>
      )}

      {stand.art === 'bestaetigt' && (
        <button
          type="button"
          className="eds-button eds-button--primary"
          onClick={() => dialog.current?.close()}
        >
          Verstanden
        </button>
      )}
    </dialog>
  );
}
